<script setup lang="ts">
import { Page, Section } from "@/components/Ui";
import { CardHero, CardWithAuth, CardFaq, CardToolbar } from "@/components/Cards";
import { BaseAffiliate } from "@/components/Sections";
import {
  BlockCardAfterSignupV2,
  BlockNoLimits,
  BlockCryptoTopUp
} from "@/components/Blocks";
import { cards, type TCard } from "@/config/cards";
import { faqForCards } from "@/config/faq";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { onEvent, onYMEvent } from "@/helpers/events";

useHead({
  htmlAttrs: {
    class: "default-container"
  }
});
const { t } = useI18n();
const cardType = "adv";
const direction = ref<string | null>(null);
const card = computed<any>(() => {
  return cards.find((card: { type: string }) => card.type === cardType);
});
const faqItems = computed<any>(() => {
  return faqForCards.find((card: { type: string }) => card.type === cardType)?.items;
});
const toolbarCards = computed<TCard[]>(() => {
  return cards.filter((card: { hidden: boolean }) => !card.hidden).sort(function(x, y) {
    return x.type === "ultima" ? -1 : y.type === "ultima" ? 1 : 0;
  }).filter((card: { type: string }) => card.type !== "black");
});
onMounted(() => {
  onEvent("landing_advertisement_page-opened");
  if (window && window.ym) {
    onYMEvent("advertisement_page-opened");
  }
});
onNuxtReady(() => {
  // Detect scroll up
  let oldScrollY = window.scrollY;
  window.onscroll = function() {
    if (oldScrollY < window.scrollY) {
      direction.value = "down";
    } else {
      direction.value = "up";
    }
    oldScrollY = window.scrollY;
  };
});
watch(
  () => direction.value,
  (newVal, oldVal) => {
    const root = document.documentElement;
    root.classList.add(`dir-${newVal}`);
    root.classList.remove(`dir-${oldVal}`);
  }
);
onUnmounted(() => {
  const root = document.documentElement;
  root.classList.remove(`dir-${direction.value}`);
  direction.value = null;
});

useSeoMeta({
  title: t("page.cardsAdv.seo.title"),
  description: t("page.cardsAdv.seo.description"),
  ogTitle: t("page.cardsAdv.seo.title"),
  ogDescription: t("page.cardsAdv.seo.description")
});
</script>
<template>
  <Page :class="$style.root">
    <CardToolbar
      :cards="toolbarCards"
      :is-down="direction === 'down'"
      :active="cardType"
      :class="`${$style['toolbar']} ${$style[`toolbar-dir-${direction}`]}`"
    />
    <CardHero
      label="Advertisement"
      label-icon="ads"
      :card="card"
      button-event="landing_advertisement_button-1"
    >
      <template #title>
        {{ $t(`cards.${cardType}HeroTitle`) }}
      </template>
      <template #description>
        {{ $t(`cards.${cardType}HeroDesc`) }}
      </template>
    </CardHero>
    <Section :title-class="$style['root-h2']">
      <template #title>
        {{ $t("cards.instantCardIssuance") }}
      </template>
      <CardWithAuth
        :card="card"
        google-button-event="landing_advertisement_button-2.1"
        telegram-button-event="landing_advertisement_button-2.2"
        email-button-event="landing_advertisement_button-2.3"
      />
      <div :class="$style.features">
        <BlockCardAfterSignupV2 :type="card.type" />
        <BlockNoLimits />
        <BlockCryptoTopUp />
      </div>
    </Section>
    <Section
      v-if="faqItems.length"
      :title-class="$style['root-h2']"
      :class="$style['root-faq']"
      full-width
    >
      <template #title>
        FAQ
      </template>
      <CardFaq :items="faqItems" class="container w-full" />
    </Section>
    <BaseAffiliate />
  </Page>
</template>
<style lang="scss" module>
.root {
  @apply flex flex-col gap-20;
  @apply mt-[70px] md:mt-[90px];
  &-h2 {
    @apply font-bold text-[36px] leading-none text-center;
  }

  &-faq {
    @apply bg-neutral-900 pt-[80px] pb-[1px] text-white  -mb-[81px] translate-y-[1px];
  }
}

.features {
  @apply grid grid-cols-1 gap-5 w-full;
  @apply lg:grid-cols-6 lg:gap-10;

  & > div {
    &:nth-child(-n+3) {
      @apply col-span-1 lg:col-span-2;
    }

    &:nth-child(n+4) {
      @apply col-span-1 lg:col-span-3;
    }
  }
}

.toolbar {
  @apply transition-all transform-gpu w-full md:sticky bg-white sm:backdrop-blur-md sm:bg-opacity-[0.85];

  &-dir {
    &-up {
      @apply md:top-[70px] z-[1000];
    }

    &-down,
    &-null {
      @apply top-0 md:z-[1100];
    }
  }
}
</style>